import { Component } from '@theme/component';

/**
 * Paginated grid — pages a grid of already-rendered items client-side.
 *
 * The section renders every item it is allowed to show (Featured collection's
 * "Products shown", for instance), and this component reveals one page of them
 * at a time, rebuilding the numbered controls out of the same `pagination__*`
 * class names the collection page uses. `snippets/client-pagination.liquid`
 * renders the nav shell and carries the matching CSS, so the controls look
 * identical wherever they are placed.
 *
 * Paging in the browser rather than with `{% paginate %}` keeps the merchant's
 * total in charge: 40 products split 10 to a page is always 4 pages, whatever
 * the collection holds, and clicking a page never reloads the page.
 *
 * Before this runs, a per-section `nth-child` rule hides everything past the
 * first page so the grid never flashes at full length; adding
 * `paginated-grid--ready` hands control over to the `hidden` attribute set here.
 *
 * @typedef {object} PaginatedGridRefs
 * @property {HTMLElement} [pagination] - The pagination nav (absent when paging is off).
 * @property {HTMLElement} [nextItem] - The `<li>` holding the next arrow; number items are inserted before it.
 * @property {HTMLButtonElement} [prev] - Previous-page arrow.
 * @property {HTMLButtonElement} [next] - Next-page arrow.
 *
 * @extends {Component<PaginatedGridRefs>}
 */
export class PaginatedGridComponent extends Component {
  /** Pages shown either side of the current one before collapsing into an ellipsis. */
  static DESKTOP_WINDOW = 2;

  /** The same window on mobile, where the controls have far less room. */
  static MOBILE_WINDOW = 1;

  #page = 1;

  /** @type {MutationObserver | undefined} */
  #contentObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#page = 1;
    this.#grid?.classList.add('paginated-grid__grid');
    this.#update();
    this.classList.add('paginated-grid--ready');

    this.refs.pagination?.addEventListener('click', this.#handleClick);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);

    const grid = this.#grid;

    // The theme editor swaps cards in and out in place. Only childList is
    // observed: the `hidden` writes below are attribute changes, so they can't
    // feed back into this.
    if (grid) {
      this.#contentObserver = new MutationObserver(() => this.#update());
      this.#contentObserver.observe(grid, { childList: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.pagination?.removeEventListener('click', this.#handleClick);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.#contentObserver?.disconnect();
  }

  /**
   * @returns {HTMLElement | null} The element whose children are the paged
   * items — the grid itself, not the wrapper this component sits on.
   */
  get #grid() {
    const selector = this.dataset.gridSelector || '.resource-list--grid';

    return this.querySelector(selector);
  }

  /** @returns {HTMLElement[]} One element per item in the grid. */
  get #items() {
    const grid = this.#grid;

    if (!grid) return [];

    return /** @type {HTMLElement[]} */ ([...grid.children]).filter((child) => child instanceof HTMLElement);
  }

  /** @returns {number} Items per page, or 0 when paging is switched off. */
  get #perPage() {
    return Math.max(0, Number.parseInt(this.dataset.perPage ?? '0', 10) || 0);
  }

  /** Shows the current page and rebuilds the controls. */
  #update() {
    const items = this.#items;
    const perPage = this.#perPage;
    const pages = perPage > 0 ? Math.max(1, Math.ceil(items.length / perPage)) : 1;

    this.#page = Math.min(Math.max(this.#page, 1), pages);

    const start = perPage > 0 ? (this.#page - 1) * perPage : 0;
    const end = perPage > 0 ? start + perPage : items.length;

    items.forEach((item, index) => {
      item.toggleAttribute('hidden', !(index >= start && index < end));
    });

    this.#renderPagination(pages);
  }

  /**
   * @param {number} pages - Total number of pages.
   */
  #renderPagination(pages) {
    const { pagination, nextItem, prev, next } = this.refs;

    if (!pagination || !nextItem) return;

    if (pages <= 1) {
      pagination.hidden = true;
      return;
    }

    pagination.hidden = false;
    // Read back by #pageItem to decide which numbers mobile can drop.
    pagination.dataset.pages = String(pages);

    for (const item of pagination.querySelectorAll('[data-pg-part]')) item.remove();

    for (const part of this.#parts(pages)) {
      nextItem.before(part === 'gap' || part === 'gap-mobile' ? this.#gapItem(part) : this.#pageItem(part));
    }

    if (prev) prev.disabled = this.#page === 1;
    if (next) next.disabled = this.#page === pages;
    prev?.classList.toggle('pagination__link--disabled', this.#page === 1);
    next?.classList.toggle('pagination__link--disabled', this.#page === pages);
  }

  /**
   * Builds the sequence of page numbers and ellipses, mirroring
   * `snippets/pagination-controls.liquid`: a `gap` collapses pages on every
   * screen, a `gap-mobile` only stands in for the numbers that the narrower
   * mobile window drops.
   *
   * @param {number} pages - Total number of pages.
   * @returns {(number | 'gap' | 'gap-mobile')[]} The parts to render, in order.
   */
  #parts(pages) {
    const { DESKTOP_WINDOW, MOBILE_WINDOW } = /** @type {typeof PaginatedGridComponent} */ (this.constructor);
    const current = this.#page;

    /** @type {(number | 'gap' | 'gap-mobile')[]} */
    const parts = [];
    let lastNumber = 0;
    let lastMobileNumber = 0;
    // A shared ellipsis already stands in for the mobile jump too, so mobile
    // must not add a second one right next to it.
    let pendingGap = false;

    for (let page = 1; page <= pages; page++) {
      const edge = page === 1 || page === pages;

      if (!edge && Math.abs(page - current) > DESKTOP_WINDOW) continue;

      if (lastNumber > 0 && page > lastNumber + 1) {
        parts.push('gap');
        pendingGap = true;
      } else if (!pendingGap && lastMobileNumber > 0 && page > lastMobileNumber + 1) {
        // Numbers between these two are on screen for desktop but hidden on
        // mobile, so mobile needs its own ellipsis to bridge the jump.
        parts.push('gap-mobile');
        pendingGap = true;
      }

      parts.push(page);
      lastNumber = page;

      if (edge || Math.abs(page - current) <= MOBILE_WINDOW) {
        lastMobileNumber = page;
        pendingGap = false;
      }
    }

    return parts;
  }

  /**
   * @param {number} page - The page this control jumps to.
   * @returns {HTMLLIElement}
   */
  #pageItem(page) {
    const { MOBILE_WINDOW } = /** @type {typeof PaginatedGridComponent} */ (this.constructor);
    const current = this.#page;
    const item = document.createElement('li');

    item.dataset.pgPart = '';
    item.className = 'pagination__item';

    const total = Number.parseInt(this.refs.pagination?.dataset.pages ?? '0', 10);
    if (page !== 1 && page !== total && Math.abs(page - current) > MOBILE_WINDOW) {
      item.classList.add('pagination__item--mobile-hide');
    }

    if (page === current) {
      const label = document.createElement('span');

      label.className = 'pagination__link pagination__link--page pagination__link--current';
      label.setAttribute('aria-current', 'page');
      label.textContent = String(page);
      item.append(label);
    } else {
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'pagination__link pagination__link--page';
      button.dataset.pgPage = String(page);
      button.setAttribute('aria-label', (this.dataset.pageLabel ?? 'Page __n__').replace('__n__', String(page)));
      button.textContent = String(page);
      item.append(button);
    }

    return item;
  }

  /**
   * @param {'gap' | 'gap-mobile'} kind - Whether the ellipsis shows everywhere or on mobile only.
   * @returns {HTMLLIElement}
   */
  #gapItem(kind) {
    const item = document.createElement('li');

    item.dataset.pgPart = '';
    item.className = `pagination__item pagination__item--gap${
      kind === 'gap-mobile' ? ' pagination__item--mobile-only' : ''
    }`;
    item.innerHTML = '<span class="pagination__link pagination__link--gap" aria-hidden="true">&hellip;</span>';

    return item;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const control = target.closest('[data-pg-page], [data-pg-step]');

    if (!(control instanceof HTMLElement)) return;

    const page = control.dataset.pgPage
      ? Number.parseInt(control.dataset.pgPage, 10)
      : this.#page + Number.parseInt(control.dataset.pgStep ?? '0', 10);

    this.#goTo(page);
  };

  /** @param {number} page */
  #goTo(page) {
    if (page === this.#page) return;

    this.#page = page;
    this.#update();

    if (this.dataset.scroll !== 'true') return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
  }

  /**
   * Theme editor: selecting a block that lives on another page would otherwise
   * highlight something the merchant can't see, so jump to its page first.
   *
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const target = event.target;
    const perPage = this.#perPage;

    if (perPage <= 0 || !(target instanceof Node) || !this.contains(target)) return;

    const index = this.#items.findIndex((item) => item.contains(target));

    if (index < 0) return;

    this.#goTo(Math.floor(index / perPage) + 1);
  };
}

if (!customElements.get('paginated-grid')) {
  customElements.define('paginated-grid', PaginatedGridComponent);
}
